import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { InlineAlert } from '../../components/ui/InlineAlert';
import {
  MyPerformanceFeedbackTarget,
  createMyCycleFeedback,
  listMyFeedbackTargets,
} from '../../services/peoplePerformanceApi';

export function CycleFeedbackPage() {
  const { cycleId } = useParams();
  const [searchParams] = useSearchParams();
  const calendarId = searchParams.get('calendarId') ?? undefined;
  const navigate = useNavigate();

  const [targets, setTargets] = useState<MyPerformanceFeedbackTarget[]>([]);
  const [targetUserId, setTargetUserId] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!cycleId) return;
    void (async () => {
      setLoading(true);
      try {
        const result = await listMyFeedbackTargets(cycleId, calendarId);
        setTargets(result);
        if (result.length > 0) {
          setTargetUserId(result[0].userId);
        }
      } catch {
        toast.error('Falha ao carregar destinatários do feedback.');
        setTargets([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [cycleId, calendarId]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId || !targetUserId.trim() || !message.trim()) return;

    const selected = targets.find((item) => item.userId === targetUserId);
    setSaving(true);
    try {
      await createMyCycleFeedback(cycleId, {
        targetUserId,
        evaluationId: selected?.evaluationId ?? null,
        message: message.trim(),
      });
      toast.success('Feedback enviado.');
      navigate('/performance');
    } catch {
      toast.error('Falha ao enviar feedback.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageShell>
      <PageHeader
        title="Feedback do ciclo"
        description="Registre um feedback formal para um participante deste calendário de coleta."
        actions={(
          <Link to="/performance" className="erp-btn-secondary text-sm">
            Voltar ao desempenho
          </Link>
        )}
      />

      {loading ? (
        <p className="text-secondary-500">Carregando...</p>
      ) : targets.length === 0 ? (
        <InlineAlert>
          Não há destinatários disponíveis para feedback neste calendário.
        </InlineAlert>
      ) : (
        <form onSubmit={(event) => void handleSubmit(event)} className="erp-card max-w-2xl">
          <div className="erp-card-body space-y-4">
            <label className="erp-label block">
              Destinatário
              <select
                className="erp-input mt-1"
                required
                value={targetUserId}
                onChange={(event) => setTargetUserId(event.target.value)}
              >
                {targets.map((target) => (
                  <option key={target.userId} value={target.userId}>
                    {target.userName || target.userId}
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label block">
              Mensagem
              <textarea
                className="erp-input mt-1"
                rows={6}
                required
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                placeholder="Descreva o feedback de forma objetiva e construtiva..."
              />
            </label>
            <div className="erp-btn-actions">
              <button type="submit" className="erp-btn-primary" disabled={saving}>
                {saving ? 'Enviando...' : 'Enviar feedback'}
              </button>
            </div>
          </div>
        </form>
      )}
    </PageShell>
  );
}
